import { DepartmentsEnum } from '@moonpig/web-core-types-graphql'
import { LocaleTextHook } from '@moonpig/web-core-locale-text'
import { DepartmentType } from '../departmentMap'
import { NavigationalSuggestionType } from '../NavigationalSuggestions/NavigationalSuggestion'
import {
  NavigationalRow,
  SearchFallbackItemType,
  SearchFallbackRow,
  SearchRow,
  SuggestionItemType,
  SuggestionRow,
  SuggestionType,
  UrlSuggestionItemType,
} from '../types'
import { rowId, staticRowId } from './utils'
import { mergeSuggestionsByScore } from './mergeSuggestionsByScore'
import { NAV_TEXT } from '../../locale'

export const buildAutocompleteSection = ({
  inputValue,
  activeDepartment,
  suggestions,
  navigationalSuggestions,
  maxSuggestions,
  maxNavigationalSuggestions,
  startIndex,
  localiseText,
}: {
  inputValue: string | null
  activeDepartment: DepartmentType
  suggestions: SuggestionType[]
  navigationalSuggestions: NavigationalSuggestionType[]
  maxSuggestions: number
  maxNavigationalSuggestions: number
  startIndex: number
  localiseText: LocaleTextHook<typeof NAV_TEXT>
}) => {
  let index = startIndex
  let staticRowIndex = 0
  let numberOfItems = 0

  const autoSectionId = 'autocomplete'
  let autocompleteRows: SearchRow[] = []

  const filterByDept = (deptEnum: DepartmentsEnum) =>
    suggestions.filter(s => s.department.enum === deptEnum)

  const cards = filterByDept(DepartmentsEnum.ALL_CARDS)
  const gifts = filterByDept(DepartmentsEnum.ALL_GIFTS)
  const flowers = filterByDept(DepartmentsEnum.ALL_FLOWERS_AND_PLANTS)

  const allDepartmentsSuggestions = mergeSuggestionsByScore({
    suggestions,
    maxSuggestions,
  })

  const addSuggestionGroup = (group: SuggestionType[]) => {
    if (!group.length) {
      return []
    }

    return group.slice(0, maxSuggestions).map(suggestion => {
      numberOfItems += 1
      const item: SuggestionItemType = {
        itemType: 'suggestion',
        value: suggestion.value,
        searchKey: suggestion.searchKey,
        department: {
          name: suggestion.department.name,
          title: suggestion.department.title.toLowerCase(),
          enum: suggestion.department.enum,
          numberOfItems: Math.min(group.length, maxSuggestions),
          activeDepartment: {
            name: activeDepartment.name,
            enum: activeDepartment.enum,
          },
        },
      }
      index += 1
      return {
        id: rowId(index),
        kind: 'suggestion',
        index,
        item,
        sectionId: autoSectionId,
      } as SuggestionRow
    })
  }

  switch (activeDepartment.name) {
    case 'all_cards':
      autocompleteRows = addSuggestionGroup(cards)
      break
    case 'all_gifts':
      autocompleteRows = addSuggestionGroup(gifts)
      break
    case 'all_flowers_and_plants':
      autocompleteRows = addSuggestionGroup(flowers)
      break
    case 'all':
      autocompleteRows = addSuggestionGroup(allDepartmentsSuggestions)
      break
  }

  const totalSuggestionCount = allDepartmentsSuggestions.length
  const showNavigationalRows =
    navigationalSuggestions.length &&
    totalSuggestionCount < maxSuggestions - maxNavigationalSuggestions

  if (showNavigationalRows) {
    numberOfItems += Math.min(
      navigationalSuggestions.length,
      maxNavigationalSuggestions,
    )
    autocompleteRows.push({
      id: staticRowId(staticRowIndex),
      kind: 'header',
      sectionId: autoSectionId,
      title: localiseText(
        'search.navigational_suggestions_header',
      ).toUpperCase(),
    })
    staticRowIndex += 1

    navigationalSuggestions
      .slice(0, maxNavigationalSuggestions)
      .forEach(nav => {
        const item: UrlSuggestionItemType = {
          itemType: 'navigational',
          value: nav.value,
          icon: nav.icon,
          url: nav.url,
        }
        index += 1
        const row: NavigationalRow = {
          id: rowId(index),
          kind: 'navigational',
          index,
          item,
          sectionId: autoSectionId,
        }

        autocompleteRows.push(row)
      })
  }

  const fallbackItem: SearchFallbackItemType = {
    itemType: 'search-fallback',
    value: inputValue || /* istanbul ignore next */ '',
    department: {
      activeDepartment: {
        name: activeDepartment.name,
        enum: activeDepartment.enum,
      },
      name: activeDepartment.name,
      title: activeDepartment.title,
      enum: activeDepartment.enum,
      numberOfItems,
    },
  }
  index += 1
  const fallbackRow: SearchFallbackRow = {
    id: `fallback:${inputValue || /* istanbul ignore next */ ''}`,
    kind: 'search-fallback',
    index,
    item: fallbackItem,
    sectionId: autoSectionId,
  }

  autocompleteRows.push(fallbackRow)

  return autocompleteRows
}
